import { FADE_THRESHOLD, PHASE_SHIFT_CEILING, PHASE_SHIFT_DURATION, REHEAT_PHASE_SHIFT_THRESHOLD, REHEAT_TEMPERATURE_CAP, WARMING_EXPIRY_COOLING_DURATION, } from './constants.js';
import { computeNextAnchorDate, computeTemperature } from './curve.js';
/**
 * Checks if a warming item has passed its anchor date and transitions to cooling.
 * Returns the item unchanged if no transition is needed.
 */
export function checkWarmingExpiry(item, now) {
    if (item.curve.direction !== 'warming')
        return item;
    if (!item.curve.anchorDate)
        return item;
    if (now < item.curve.anchorDate)
        return item;
    return {
        ...item,
        curve: {
            direction: 'cooling',
            startingTemperature: item.curve.ceiling,
            easing: 'ease-in',
            duration: WARMING_EXPIRY_COOLING_DURATION,
        },
        createdAt: item.curve.anchorDate,
    };
}
/**
 * Checks if an active item has dropped below the fade threshold.
 * Returns the item with status set to 'fading' if so.
 */
export function checkFading(item, temperature) {
    if (item.status !== 'active')
        return item;
    if (temperature >= FADE_THRESHOLD)
        return item;
    return { ...item, status: 'fading' };
}
/**
 * Handles the "still matters" reheat action on a cooling/fading item.
 * After REHEAT_PHASE_SHIFT_THRESHOLD reheats, transitions from cooling to warming.
 */
export function handleReheat(item, now) {
    const newReheatCount = item.reheatCount + 1;
    const baseUpdate = {
        ...item,
        status: 'active',
        reheatCount: newReheatCount,
        lastInteractedAt: now,
    };
    if (item.curve.direction === 'cooling' && newReheatCount >= REHEAT_PHASE_SHIFT_THRESHOLD) {
        return {
            ...baseUpdate,
            reheatCount: 0,
            curve: {
                direction: 'warming',
                startingTemperature: computeTemperature(item.curve, item.createdAt, now),
                ceiling: PHASE_SHIFT_CEILING,
                easing: 'ease-in',
                duration: PHASE_SHIFT_DURATION,
            },
            createdAt: now,
        };
    }
    // Regular reheat — reset temperature on the existing cooling curve
    return {
        ...baseUpdate,
        curve: {
            ...item.curve,
            startingTemperature: Math.min(item.curve.startingTemperature, REHEAT_TEMPERATURE_CAP),
        },
        createdAt: now,
    };
}
/**
 * Handles item completion, including recurrence logic.
 * Non-recurring items become 'completed'.
 * Recurring items get a new curve but keep the same identity.
 */
export function handleCompletion(item, now) {
    if (!item.recurrence) {
        return { ...item, status: 'completed', lastCompletedAt: now };
    }
    if (item.recurrence.type === 'fixed') {
        const nextAnchor = computeNextAnchorDate(now, item.recurrence.interval, item.recurrence.unit);
        return {
            ...item,
            lastCompletedAt: now,
            curve: {
                ...item.recurrence.curve,
                anchorDate: nextAnchor,
            },
            createdAt: now,
        };
    }
    // item.recurrence.type === 'loose'
    return {
        ...item,
        lastCompletedAt: now,
        curve: {
            direction: 'warming',
            startingTemperature: 0,
            ceiling: item.recurrence.ceiling,
            easing: item.recurrence.easing,
            duration: item.recurrence.restPeriod + item.recurrence.rampDuration,
        },
        createdAt: now,
    };
}
//# sourceMappingURL=transitions.js.map